/* eslint-disable max-len */
import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import { GreenroomState } from '../../../stores/types/types';
import Switch from '../../general-components/switch';
import * as Storage from '../../../utils/local-storage';
import { DebugLocalStorage } from './panel';
import { BROADCAST_STATES } from '../../../stores/types/broadcasting';

const pattern = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

export default function DebugToggles() {
	const dispatch = useDispatch();

	const {
		debugEnabled,
		debugOverlaysEnabled,
		debugEchoCancellationEnabled,
		debugSocketCanDispatch,
		debugOpentok,
		debugKillServer,
		debugDisconnectProducer,
		debugLogIsoAlerts,
		debugAVUI,
	} = useSelector((state: GreenroomState) => state.DebugReducer);

	const setOverlaysOn = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.OverlaysOn, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_OVERLAYS', payload: isEnabled });
		}
	};

	const toggleEchoCancellation = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.EchoCancellationOn, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_ECHO_CANCELLATION', payload: isEnabled });
		}
	};

	const toggleSocketCanDispatch = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.SocketCanDispatch, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_SOCKET_DISPATCH', payload: isEnabled });
		}
	};

	const toggleDebugOpentok = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.DebugOpentok, isEnabled.toString());
			dispatch({ type: 'DEBUG_OPENTOK_DISPATCH', payload: isEnabled });
		}
	};
	
	const toggleDebugStylepacksFilters = (isEnabled: boolean) => {
		if (debugEnabled) {
			dispatch({ type: 'DEBUG_STYLEPACKS_FILTER', payload: isEnabled });
		}
	};

	const toggleDebugKillServer = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.debugKillServer, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_KILL_SERVER', payload: isEnabled });
			if (!debugKillServer) {
				dispatch(BroadcastActions.setBroadcastState(BROADCAST_STATES.KILLED));
			} else {
				dispatch(BroadcastActions.setBroadcastState(BROADCAST_STATES.BROADCAST_STOPPED));
			}
		}
	};

	const phrases = useRef(['Hello world.', 'https://www.youtube.com/watch?v=dQw4w9WgXcQ']);
	useEffect(() => { if (new Date().getDay() === 3) phrases.current.push('It is Wednesday my dudes'); }, []);
	const rand = (list: Array<any>) => list[Math.floor((Math.random() * list.length))];
	const [konamiOn, setKonami] = useState<boolean>(false);
	const match = useRef<number>(0);
	const konami = (e: any) => {
		match.current = (pattern.indexOf(e.key) < 0 || e.key !== pattern[match.current]) ? 0 : match.current + 1;
		if (match.current === pattern.length) { console.log(rand(phrases.current)); match.current = 0; }
	};
	useEffect(() => {
		if (konamiOn) document.addEventListener('keydown', konami, false);
		else document.removeEventListener('keydown', konami);
		return () => document.removeEventListener('keydown', konami);
	}, [konamiOn]);

	const toggleDebugDisconnectProducer = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.debugDisconnectProducer, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_DISCONNECT_PRODUCER', payload: isEnabled });
		}
	};

	const toggleDebugLogIsoAlerts = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.debugLogIsoAlerts, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_LOG_ISO_ALERTS', payload: isEnabled });
		}
	};

	const toggleAVUI = (isEnabled: boolean) => {
		if (debugEnabled) {
			Storage.setStorageItem(DebugLocalStorage.avUI, isEnabled.toString());
			dispatch({ type: 'DEBUG_AV_UI', payload: isEnabled });
		}
	};
	const boolStr = useCallback((input: boolean): boolean => String(input).toLowerCase() === 'true', []);

	useEffect(() => {
		// Load Settings
		const overlaysOn = Storage.getStorageItem(DebugLocalStorage.OverlaysOn);
		const echoCancellationOn = Storage.getStorageItem(DebugLocalStorage.EchoCancellationOn);
		const socketCanDispatch = Storage.getStorageItem(DebugLocalStorage.SocketCanDispatch);
		const debugOpentokOn = Storage.getStorageItem(DebugLocalStorage.DebugOpentok);

		// On by default
		setOverlaysOn(overlaysOn !== null ? boolStr(overlaysOn) : true);
		toggleEchoCancellation(echoCancellationOn !== null ? boolStr(echoCancellationOn) : true);
		toggleSocketCanDispatch(socketCanDispatch !== null ? boolStr(socketCanDispatch) : true);
		toggleDebugOpentok(debugOpentokOn !== null ? boolStr(debugOpentokOn) : true);
	}, []);

	return (
		<div className="section settings">
			<h5>Toggles</h5>
			<div className="advanced-settings-container">
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>AV UI Widget</span>
					<Switch
						on={debugAVUI}
						onChange={() => toggleAVUI(!debugAVUI)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Overlays</span>
					<Switch
						on={debugOverlaysEnabled}
						onChange={() => setOverlaysOn(!debugOverlaysEnabled)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Echo Cancellation</span>
					<Switch
						on={debugEchoCancellationEnabled}
						onChange={() => toggleEchoCancellation(!debugEchoCancellationEnabled)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Socket can fire dispatch</span>
					<Switch
						on={debugSocketCanDispatch}
						onChange={() => toggleSocketCanDispatch(!debugSocketCanDispatch)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Debug Opentok</span>
					<Switch
						on={debugOpentok}
						onChange={() => toggleDebugOpentok(!debugOpentok)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Disconnect Producer</span>
					<Switch
						on={debugDisconnectProducer}
						onChange={() => toggleDebugDisconnectProducer(!debugDisconnectProducer)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Kill Server 💀</span>
					<Switch
						on={debugKillServer}
						onChange={() => toggleDebugKillServer(!debugKillServer)}
						className={'green'}
					/>
				</div>
				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Konami? 👾</span>
					<Switch
						on={konamiOn}
						onChange={() => setKonami((prev) => !prev)}
						className={'green'}
					/>
				</div>

				<div className="align-items-center justify-content-space-between vertical-padding-small">
					<span>Log timestamps of ISO Alerts</span>
					<Switch
						on={debugLogIsoAlerts}
						onChange={() => toggleDebugLogIsoAlerts(!debugLogIsoAlerts)}
						className={'green'}
					/>
				</div>
			</div>
		</div>
	);
}
